/** @jsxImportSource @emotion/react */
import { UserPermission } from 'components/lab/user/permissions'
import { useAppSelector } from 'hooks/redux'
import { Order } from 'store/types'
import { getEditablePayers, getOutsourceHiddenReason } from './outsourceVisibility'

/**
 * Purely informational banner shown to (non-outsource) staff whenever an order is hidden from
 * outsource users by the "Outsource: Hide Medicare Advantage / Medicaid Orders" access policy.
 *
 * It explains the exact reason the order is hidden. If the acting user is allowed to edit
 * eligibility flags, it also hints that they can flip the Medicare Advantage / Medicaid flags in
 * the Insurance section below to correct a misclassification. It has no actions of its own — the
 * only way to change visibility is to correct the flags on the verification itself.
 */
export const OutsourceAccessWarning = ({ order }: { order?: Order }) => {
  const canModifyFlags = useAppSelector(
    (state) =>
      state.init.isSuperadmin === true ||
      state.init.permissions.includes(UserPermission.MODIFY_INSURANCE_ELIGIBILITY_FLAGS),
  )

  const reason = getOutsourceHiddenReason(order)
  if (!order || !reason) {
    return null
  }

  // Only hint at "switch the flags below" when there is an editable verification to switch. This
  // covers null-flag orders (COX/PSG, AI-disabled, failed lambdas) too — they can be granted by
  // clearing the flags — but not orders with no verification row, which have nothing to edit.
  const hasFlagsToSwitch = getEditablePayers(order).length > 0

  return (
    <div
      css={{
        padding: '14px 16px',
        marginTop: 12,
        borderRadius: 10,
        border: '1px solid #f0dca6',
        background: '#fff8e8',
        color: '#7a5a17',
        fontSize: 13,
      }}>
      <div css={{ fontWeight: 700, color: '#9a6a10', marginBottom: 3 }}>
        Outsource staff can’t see this order
      </div>
      <div>
        This order is hidden from outsource users because {reason}.
        {canModifyFlags && hasFlagsToSwitch && (
          <>
            {' '}
            If you think that was a mistake, manually switch the insurance verification flags below.
          </>
        )}
      </div>
    </div>
  )
}
